import { LitElement, html, TemplateResult, nothing } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { FontSizes, FontWeights, LabelWidthValues } from '../../../../../types/common/types';
import { translateFn } from '../../../../../utils/translator.helper';
import { API_TYPE_DEVICE_FILTER_RISK_MANAGEMENT } from '../../../../../webcomponents.constants';
import { GzSingleSelectComplexWebComponent } from '../../../../../ui/gzSingleSelectComplex';
import {
  getFindingsDetailsItemType,
  getIconNameItemType,
  renderCategories,
  renderEmptyDeviceDetailsState,
  renderErrorDeviceDetailsState,
  RiskManagementSideMenuEvents,
  setFilterOptionsForDeviceDropdown,
} from '../../../common/common';
import { InitialDropdownDetails } from '../types/types';

/**
 * @summary Type here the description of this component
 * @tag device-details
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('device-details')
export class DeviceDetailsWebComponent extends LitElement {
  /**
   * See https://lit-element.polymer-project.org/guide/properties
   * Write here the description of this field
   */
  @property({ type: String, attribute: true, reflect: true })
  detailsTitle = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  showDeviceDropdown = false;

  @property({ type: Object, attribute: false })
  initialDropdownDetails = null as null | InitialDropdownDetails;

  @property({ type: Object, attribute: false })
  deviceInformation = null as null | any;

  @property({ type: Boolean, attribute: true, reflect: true })
  hasErrors = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  emptyState = false;

  @property({ type: String, attribute: false })
  extraDetailsPanelTitle = null as string | null;

  @property({ type: String, attribute: false })
  companyId = null as string | null;

  @query('#device-details-selection')
  public readonly deviceDetailsDropdown!: GzSingleSelectComplexWebComponent;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  updated(changedProperties): void {
    super.updated(changedProperties);

    if (changedProperties.has('initialDropdownDetails')) {
      this.updateComplete.then(() => {
        setFilterOptionsForDeviceDropdown(this);
      });
    }
  }

  render() {
    if (this.emptyState) {
      return nothing;
    }
    return html`${this.renderTitle()} ${this.renderDeviceComponents()}`;
  }

  private renderTitle() {
    if (!this.detailsTitle) {
      return nothing;
    }

    return html`<gz-title
      fontSize="${FontSizes.PARAGRAPH_11}"
      fontWeight="${FontWeights.SEMIBOLD}"
      class="section-title text-uppercase mb-2"
      >${this.detailsTitle}
    </gz-title>`;
  }

  private renderDeviceComponents() {
    if (this.hasErrors) {
      return renderErrorDeviceDetailsState();
    }

    if (this.showDeviceDropdown && !this.initialDropdownDetails) {
      return html`<gz-loader></gz-loader>`;
    }

    return html` ${this.renderDeviceDropdown()} ${this.renderDeviceDetails()} `;
  }

  private renderDeviceDropdown() {
    if (!this.showDeviceDropdown) {
      return nothing;
    }

    return html` <gz-single-select-complex
      id="device-details-selection"
      class="pb-1 w-full device-details-selection"
      .apiType=${API_TYPE_DEVICE_FILTER_RISK_MANAGEMENT}
      .label=${'Resource name'}
      .labelWidth=${LabelWidthValues.FULL}
      .noSearchResultsString=${translateFn('No results')}
      .confirmActionButtonString=${translateFn('Apply')}
      .searchPlaceholderString=${translateFn('Search here')}
      .errorMessage=${translateFn('Error')}
      @selectionChange=${this.handleDropdownValueChanged}
      isFilter
      cannotDelete="true"
    ></gz-single-select-complex>`;
  }

  private renderDeviceDetails() {
    if (!this.deviceInformation) {
      return html` <gz-loader></gz-loader>`;
    }

    if (Object.keys(this.deviceInformation).length === 0) {
      return renderEmptyDeviceDetailsState(this.showDeviceDropdown);
    }

    const finalTemplate: any = [];

    for (const [category, information] of Object.entries(this.deviceInformation)) {
      const categoryNumber = parseInt(category, 10);
      if (!Array.isArray(information)) {
        continue;
      }

      const deviceTitle: string = getFindingsDetailsItemType(categoryNumber);
      const deviceNumber: string = '(' + information.length + ')';
      const deviceIconClass: string = getIconNameItemType(categoryNumber);

      const deviceDetailsCategoryInformation: TemplateResult = html`
        ${renderCategories(deviceIconClass, deviceTitle, deviceNumber, async () => {
          this.handleDeviceExtraDetailsButtonClick(information, categoryNumber);
        })}
      `;

      finalTemplate.push(deviceDetailsCategoryInformation);
    }

    return finalTemplate;
  }

  private handleDeviceExtraDetailsButtonClick(items, category) {
    const eventDetail = {
      showExtraDetailsPanel: true,
      items,
      itemsType: category,
      companyId: this.initialDropdownDetails?.companyId ?? this.companyId,
    };

    if (this.showDeviceDropdown) {
      const currentSelectedDeviceFromDropdown = this.deviceDetailsDropdown.getSelectedOption();

      eventDetail['dropdownDetails'] = {
        endpointName: currentSelectedDeviceFromDropdown?.label,
        endpointId: currentSelectedDeviceFromDropdown?.value,
        companyId: this.initialDropdownDetails?.companyId,
        endpointIds: this.initialDropdownDetails?.endpointIds,
        uniqueIdentifierForFinding: this.initialDropdownDetails?.uniqueIdentifierForFinding,
      };
    }

    if (this.extraDetailsPanelTitle) {
      eventDetail['extraDetailsPanelTitle'] = this.extraDetailsPanelTitle;
    }

    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.DEVICE_DETAILS_SHOW_EXTRA_DATA, {
        detail: eventDetail,
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private handleDropdownValueChanged(event) {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.DEVICE_DETAILS_CHANGED, {
        detail: {
          endpointId: event.detail.selectedValue,
          companyId: this.initialDropdownDetails?.companyId,
          uniqueIdentifierForFinding: this.initialDropdownDetails?.uniqueIdentifierForFinding,
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'device-details': DeviceDetailsWebComponent;
  }
}
